<script setup lang="ts">
import { useGetReportTemplate } from "@/stores/queries/report-templates.query";
import { computed, ref, toRef, watch } from "vue";
import DRBtnIcon from "../UI/DRBtnIcon.vue";
import TemplateIcon from "../UI/icons/TemplateIcon.vue";
import TemplateActionsDropdown from "./TemplateActionsDropdown.vue";
import { useScreen } from "@/composable/useResponsive";
import TemplateDrawerFieldsContent from "./tabs/fields/TemplateDrawerFieldsContent.vue";
import TemplateDrawerSettingsContent from "./tabs/settings/TemplateDrawerSettingsContent.vue";
import TemplateDrawerSettingsFooter from "./tabs/settings/TemplateDrawerSettingsFooter.vue";
import TemplateDrawerSectionsContent from "./tabs/sections/TemplateDrawerSectionsContent.vue";

const props = withDefaults(
  defineProps<{
    templateId?: number;
    hideActions?: boolean;
    initialTab?: string;
  }>(),
  {
    hideActions: false,
  }
);

const templateId = toRef(props, "templateId");

enum TemplateTab {
  DetectedFields = "detectedFields",
  Sections = "sections",
  Settings = "settings",
}

const emits = defineEmits<{
  (e: "close"): void;
}>();

const show = computed({
  get: () => !!templateId.value,
  set: (val: boolean) => {
    if (!val) emits("close");
  },
});

const { data: template, isLoading } = useGetReportTemplate(templateId);

const { screen } = useScreen();

const currentTabValue = ref<TemplateTab>(TemplateTab.DetectedFields);

watch(show, (visible) => {
  if (
    visible &&
    props.initialTab &&
    Object.values(TemplateTab).includes(props.initialTab as TemplateTab)
  ) {
    currentTabValue.value = props.initialTab as TemplateTab;
  }
});

const tabs: Record<TemplateTab, any> = {
  [TemplateTab.DetectedFields]: {
    icon: "bi-search",
    content: TemplateDrawerFieldsContent,
  },
  [TemplateTab.Sections]: {
    icon: "bi-view-stacked",
    content: TemplateDrawerSectionsContent,
  },
  [TemplateTab.Settings]: {
    icon: "bi-sliders",
    content: TemplateDrawerSettingsContent,
    footer: TemplateDrawerSettingsFooter,
  },
};

const currentTab = computed(() => {
  return tabs[currentTabValue.value];
});
</script>

<template>
  <el-drawer
    v-model="show"
    @close="emits('close')"
    :show-close="false"
    header-class="border-bottom mb-0 px-2 pt-2 "
    :size="screen.gt.sm ? '500px' : '100%'"
  >
    <template #header="{ close, titleId }">
      <div class="column overflow-hidden">
        <div class="w-100 overflow-hidden">
          <div class="d-flex flex-row align-items-center gap-3">
            <TemplateIcon :size="30" class="flex-shrink-0" />
            <div
              v-if="template"
              :titleId="titleId"
              class="h5 text-truncate pt-2 flex-grow-1"
              style="min-width: 0"
            >
              {{ template?.name ?? `${$t("loading")} ...` }}
            </div>
            <TemplateActionsDropdown
              v-if="template && !hideActions"
              :template="template"
              :size="40"
              class="flex-shrink-0"
              @deleted="close()"
            />
            <DRBtnIcon
              class="flex-shrink-0"
              icon="bi-x-lg"
              variant="flat"
              @click="close"
            />
          </div>
        </div>
        <div class="mt-3">
          <ul class="nav nav-pills nav-justified styled-tabs">
            <li
              v-for="tab in Object.values(TemplateTab)"
              :key="tab"
              class="nav-item"
            >
              <button
                class="nav-link cursor-pointer"
                :class="{
                  active: currentTabValue === tab,
                }"
                @click="currentTabValue = tab"
              >
                <div
                  class="d-flex align-items-center gap-2 justify-content-center"
                >
                  <el-icon size="1rem"><i :class="tabs[tab].icon"></i></el-icon>

                  <span>
                    {{
                      $t(`dashboard:templates.drawer.tabsTitle.${tab}`)
                    }}</span
                  >
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <div v-loading="isLoading">
      <template v-if="template">
        <component
          :key="template.id"
          :is="currentTab.content"
          :template="template"
        />
      </template>
    </div>
    <template v-if="template && currentTab.footer" #footer>
      <component
        :key="template.id"
        :is="currentTab.footer"
        :template="template"
      />
    </template>
  </el-drawer>
</template>

<style lang="scss" scoped>
.styled-tabs {
  .nav-link {
    border: none;
    border-radius: 0;
    background-color: transparent;
    color: var(--el-text-color-secondary);
    &:hover {
      background-color: var(--el-bg-color-light-3);
      color: var(--el-text-color-primary);
    }
    &.active {
      background-color: var(--el-bg-color-light-3);
      color: var(--bs-primary);
      border-bottom: 2px solid var(--bs-primary);
    }
  }
}
</style>
